import { useState, useEffect } from "react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import Dialog from "../components/dashboard/Dialog";
import { useToast } from "../components/providers/ToastProvider";
import { useSession } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

interface WaitlistEntry {
  id: string;
  userId: string;
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
    membership: string;
    highest_plan_name?: string;
    createdAt: string;
  };
}

export default function AdminVIPWaitlist() {
  const { data: session, status } = useSession();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEntry, setSelectedEntry] = useState<WaitlistEntry | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchWaitlist = async () => {
    try {
      const res = await api.get('/admin/vip-waitlist');
      setWaitlist(res.data);
    } catch (err) {
      console.error("Failed to fetch waitlist:", err);
      showToast("Error", "Failed to communicate with waitlist server.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === "unauthenticated") {
      navigate("/login");
    } else if (status === "authenticated") {
      const role = (session?.user as any)?.role;
      if (role !== "ADMIN") {
        navigate("/dashboard");
      } else {
        fetchWaitlist();
      }
    }
  }, [status, session, navigate]);

  const handlePromoteToVip = async () => {
    if (!selectedEntry) return;
    setIsSubmitting(true);
    try {
      // 1. Upgrade user tier to VIP
      await api.patch(`/admin/students/${selectedEntry.userId}/membership`, { membership: "VIP" });

      // 2. Remove entry from waitlist
      await api.delete(`/admin/vip-waitlist/${selectedEntry.id}`);

      showToast("VIP Promotion Successful", `${selectedEntry.user.name} has been promoted to VIP tier.`, "success");
      setIsUpgradeModalOpen(false);
      setSelectedEntry(null);
      fetchWaitlist();
    } catch (err: any) {
      console.error("Promotion failed:", err);
      showToast("Promotion Failed", err.response?.data?.error || "An unexpected error occurred during promotion.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveFromWaitlist = async () => {
    if (!selectedEntry) return;
    setIsSubmitting(true);
    try {
      await api.delete(`/admin/vip-waitlist/${selectedEntry.id}`);
      showToast("Waitlist Entry Removed", `${selectedEntry.user.name} has been removed from the VIP queue.`, "success");
      setIsDeleteModalOpen(false);
      setSelectedEntry(null);
      fetchWaitlist();
    } catch (err) {
      console.error("Removal failed:", err);
      showToast("System Error", "Communication error with waitlist server.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredWaitlist = waitlist.filter((entry) => {
    const name = entry.user?.name || "Unknown Student";
    const email = entry.user?.email || "";
    return (
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getTierDetails = (tier: string) => {
    switch (tier) {
      case "VIP":
        return { color: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20", icon: "auto_awesome" };
      case "PREMIUM":
        return { color: "bg-amber-500/10 text-amber-500 border-amber-500/20", icon: "workspace_premium" };
      case "GROWTH":
        return { color: "bg-slate-500/10 text-slate-500 border-slate-500/20", icon: "military_tech" };
      default:
        return { color: "bg-slate-500/10 text-slate-500 border-slate-500/20", icon: "person" };
    }
  };

  return (
    <div className="min-h-screen bg-background text-on-background">
      <Header />
      <Sidebar />

      <main className="ml-0 md:ml-20 pt-20 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto space-y-8 sm:space-y-12">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-6">
            <div className="space-y-2">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Exclusive Access Queue</span>
              <h1 className="text-3xl sm:text-5xl font-black text-on-surface tracking-tighter leading-tight">
                VIP Waiting <span className="text-outline-variant/40">List.</span>
              </h1>
            </div>
            <div className="w-full sm:w-auto px-6 py-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-center justify-center sm:justify-start gap-3">
              <span className="material-symbols-outlined text-amber-500 animate-pulse">crown</span>
              <div className="text-left">
                <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest leading-none">Queue Size</p>
                <p className="text-lg font-black text-on-surface leading-none mt-1">{waitlist.length} Students</p>
              </div>
            </div>
          </div>

          {/* Search Controls */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative group flex-1">
              <span className="material-symbols-outlined absolute left-5 sm:left-6 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">
                search
              </span>
              <input
                type="text"
                placeholder="Search waitlisted student by name or email identity..."
                className="w-full pl-12 sm:pl-16 pr-6 py-4 bg-surface-container-low border border-outline-variant/10 rounded-[1.5rem] sm:rounded-[2rem] outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all text-sm font-medium shadow-sm text-on-surface"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Waitlist Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {loading ? (
              [1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-56 bg-surface-container-low rounded-[1.75rem] sm:rounded-[2.5rem] animate-pulse border border-outline-variant/5"
                />
              ))
            ) : filteredWaitlist.length > 0 ? (
              filteredWaitlist.map((entry, index) => {
                const tier = getTierDetails(entry.user?.highest_plan_name || entry.user?.membership || "FREE");
                return (
                  <div
                    key={entry.id}
                    className="bg-surface-container-low p-6 sm:p-8 rounded-[1.75rem] sm:rounded-[2.5rem] border border-outline-variant/10 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden flex flex-col justify-between min-h-[260px]"
                  >
                    {/* Golden ambient background glow */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl group-hover:bg-amber-500/10 transition-colors" />

                    <div>
                      <div className="flex justify-between items-start mb-6 relative z-20">
                        <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center font-black text-lg">
                          #{index + 1}
                        </div>
                        <span
                          className={`px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border flex items-center gap-2 ${tier.color}`}
                        >
                          <span className="material-symbols-outlined text-[12px]">{tier.icon}</span>
                          {entry.user?.highest_plan_name || entry.user?.membership || "FREE"}
                        </span>
                      </div>

                      <div className="space-y-1 mb-6">
                        <h3 className="text-lg sm:text-xl font-black text-on-surface tracking-tight truncate group-hover:text-primary transition-colors">
                          {entry.user?.name || "Unknown Student"}
                        </h3>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 truncate">{entry.user?.email || "N/A"}</p>
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t border-outline-variant/10">
                      <div className="flex justify-between text-xs">
                        <div className="flex flex-col min-w-0">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Waitlist Join Date</span>
                          <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 truncate">
                            {new Date(entry.createdAt).toLocaleDateString()} at{" "}
                            {new Date(entry.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2.5 sm:gap-3 relative z-20">
                        <button
                          onClick={() => {
                            setSelectedEntry(entry);
                            setIsUpgradeModalOpen(true);
                          }}
                          className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-black rounded-xl font-black text-[10px] uppercase tracking-widest transition-all hover:translate-y-[-2px] hover:shadow-lg hover:shadow-amber-500/20 flex items-center justify-center gap-1.5"
                        >
                          <span className="material-symbols-outlined text-xs">upgrade</span>
                          Promote to VIP
                        </button>
                        <button
                          onClick={() => {
                            setSelectedEntry(entry);
                            setIsDeleteModalOpen(true);
                          }}
                          className="px-3 sm:px-4 py-3 bg-red-500/10 text-red-500 hover:bg-red-500/20 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full py-20 text-center space-y-4 opacity-40">
                <span className="material-symbols-outlined text-6xl text-amber-500/50">workspace_premium</span>
                <p className="text-xl font-black tracking-tight">The VIP waiting list is currently clear.</p>
                <p className="text-xs text-slate-500">All students are perfectly accommodated inside their current tiers.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Upgrade to VIP Confirmation Modal */}
      <Dialog
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        title="Promote Student to VIP"
        className="!max-w-md border-amber-500/20"
      >
        <div className="space-y-6 text-center py-2">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-amber-500/10 text-amber-500 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-3xl sm:text-4xl">crown</span>
          </div>
          <div className="space-y-2">
            <h4 className="text-lg sm:text-xl font-black text-on-surface tracking-tight">VIP Upgradability</h4>
            <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed px-2">
              You are about to promote <span className="text-on-surface font-black italic">{selectedEntry?.user.name}</span> to the <span className="text-amber-500 font-bold">VIP tier</span>. This will immediately unlock exclusive expert guidance for them and clear them from the queue.
            </p>
          </div>
          <div className="flex gap-3 sm:gap-4 pt-4">
            <button
              onClick={() => setIsUpgradeModalOpen(false)}
              className="flex-1 py-3.5 bg-surface-container-high text-on-surface-variant rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-all"
            >
              Abeyance
            </button>
            <button
              onClick={handlePromoteToVip}
              disabled={isSubmitting}
              className="flex-1 py-3.5 bg-amber-500 text-black rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl hover:shadow-amber-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                "Promote to VIP"
              )}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Remove Waitlist Entry?"
        className="!max-w-md border-red-500/20"
      >
        <div className="space-y-6 text-center py-2">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-500/10 text-red-500 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-3xl sm:text-4xl">person_remove</span>
          </div>
          <div className="space-y-2">
            <h4 className="text-lg sm:text-xl font-black text-on-surface tracking-tight">Expunge Waitlist Record</h4>
            <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed px-2">
              You are about to remove <span className="text-on-surface font-black italic">{selectedEntry?.user.name}</span> from the VIP queue. Their existing membership tier level will remain unchanged.
            </p>
          </div>
          <div className="flex gap-3 sm:gap-4 pt-4">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="flex-1 py-3.5 bg-surface-container-high text-on-surface-variant rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-all"
            >
              Abeyance
            </button>
            <button
              onClick={handleRemoveFromWaitlist}
              disabled={isSubmitting}
              className="flex-1 py-3.5 bg-red-500 text-white rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl hover:shadow-red-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                "Remove Entry"
              )}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
